import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    Typography,
    Button,
    Grid2,
    InputAdornment,
    Autocomplete
} from '@mui/material';
import ModalBox from '../themes/overrides/ModalBox';
import { useDispatch, useSelector } from 'react-redux';
import { getAllEntries } from '../actions/entryActions';
import { CircularProgress } from '@mui/material';
import TextFieldInput from '../themes/overrides/TextFieldInput';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useParams } from 'react-router-dom';

const AddExistingEntryPopup = ({ open, onClose, onConfirm, initialExistingEntries }) => {
    const dispatch = useDispatch();
    const { workspaceId } = useParams();

    const [selectedEntries, setSelectedEntries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);

    const [nextPage, setNextPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [rowsLoading, setRowsLoading] = useState(false);
    const observer = useRef();
    const ITEMS_PER_PAGE = 30;

    // Initialize with initial entries
    const [localEntries, setLocalEntries] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            setLocalEntries(null);
            setNextPage(1);
            setSelectedEntries([]);
            setSearchTerm('');
            setDebouncedSearchTerm('');
        }
    }, [open]);

    // Handle search or initial entries
    useEffect(() => {
        if (!open) return;

        if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
            setNextPage(1);
            setLocalEntries([]);
            setLoading(true);
            loadEntries(1, true);
        } else if (initialExistingEntries?.length) {
            setLocalEntries(initialExistingEntries);
            setNextPage(2);
            setHasMore(initialExistingEntries.length === ITEMS_PER_PAGE);
        } else {
            // Lazy load if no initial entries provided
            setLocalEntries([]);
            setLoading(true);
            loadEntries(1, true);
        }
    }, [open, debouncedSearchTerm, initialExistingEntries]);


    const loadEntries = async (pageNum, reset = false) => {
        if (loading || rowsLoading) return;

        try {
            setRowsLoading(true);

            const params = {
                page: pageNum,
                ...(debouncedSearchTerm.length >= 3 && { identifier: debouncedSearchTerm }),
            };

            const result = await dispatch(getAllEntries(workspaceId, params));

            if (reset) {
                setLocalEntries(result.entries || []);
            } else {
                setLocalEntries(prev => [...prev, ...(result.entries || [])]);
            }

            setHasMore((result.entries?.length || 0) === ITEMS_PER_PAGE);
            setNextPage(pageNum + 1); // Update next page to load
        } catch (error) {
            console.error("Error loading entries:", error);
        } finally {
            setLoading(false);
            setRowsLoading(false);
        }
    };

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (value.length === 0) {
            setDebouncedSearchTerm('');
        } else if (value.length >= 3) {
            searchTimeoutRef.current = setTimeout(() => {
                setDebouncedSearchTerm(value);
            }, 500);
        }
    };

    const lastElementRef = useCallback(node => {
        if (loading || rowsLoading || !hasMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadEntries(nextPage);
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, rowsLoading, hasMore, nextPage, debouncedSearchTerm]);

    const handleConfirm = () => {
        const selectedObjects = selectedEntries.map(({ _id, identifier, published, contentTypeId, content_type_name }) => ({
            _id, identifier, published, contentTypeId, content_type_name
        }));

        onConfirm(selectedObjects);
        setSelectedEntries([]);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: { xs: '95%', sm: '600px', md: '700px' }, maxHeight: { xs: '95vh', sm: '90vh' }, padding: { xs: 2, md: 4 } }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextFieldInput
                        fullWidth
                        size="small"
                        placeholder="Search entries..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setDebouncedSearchTerm('');
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {loading || localEntries === null ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 12 }}>
                        <CircularProgress />
                    </Box>
                ) : localEntries && localEntries.length === 0 ? (
                    <Typography marginTop={1} align="center" color="textSecondary">
                        {debouncedSearchTerm.length >= 3
                            ? "No entries match your search"
                            : "No entries available"}
                    </Typography>
                ) : (
                    localEntries.map((entry, index) => (
                        <Grid2
                            ref={index === localEntries.length - 1 ? lastElementRef : null}
                            key={entry._id}
                            padding={2}
                            marginTop={1}
                            container
                            onClick={() => {
                                setSelectedEntries(prev => {
                                    const exists = prev.some(e => e._id === entry._id);
                                    return exists ? prev.filter(e => e._id !== entry._id) : [...prev, entry];
                                });
                            }}
                            sx={(theme) => ({
                                cursor: 'pointer',
                                border: selectedEntries.some(e => e._id === entry._id)
                                    ? `1.5px solid ${theme.palette.primary.main}`
                                    : '1.5px solid #ECECEC',
                                borderRadius: 1,
                                transition: 'border 0.2s ease',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                            })}
                        >
                            <Grid2 size={{ xs: 8, md: 10 }}>
                                <Typography variant="h6">{entry.identifier}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {entry.content_type_name}
                                </Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 4, md: 2 }} textAlign="right">
                                <Typography
                                    sx={{
                                        color: entry.published ? 'green' : '#856404',
                                        backgroundColor: entry.published ? '#e6ffe6' : '#fff3cd',
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: 13
                                    }}
                                >
                                    {entry.published ? 'Published' : 'Draft'}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    ))
                )}
                {(loading || rowsLoading) && nextPage > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                <Box sx={{
                    position: 'sticky',
                    bottom: { xs: -25, md: -34 },
                    pt: 2,
                    pb: 2,
                    backgroundColor: 'white',
                    borderTop: '1px solid #ECECEC',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                }}>
                    <Button onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="primary"
                        disabled={selectedEntries.length === 0}
                    >
                        Done ({selectedEntries.length})
                    </Button>
                </Box>
            </ModalBox>
        </Modal>
    );
};

AddExistingEntryPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
};

export default AddExistingEntryPopup;